import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Search,
  Download,
  RefreshCw,
  FileText,
  ExternalLink,
  Eye,
  Trash2,
  X,
  Briefcase,
  Users,
  CheckCircle2,
  Clock,
  Mail,
  Phone,
  MapPin,
  Building2,
  Copy,
  Check,
  ChevronDown,
  UserCheck,
  AlertCircle,
  Calendar,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle } from '../components/ui/sheet';
import { AdminChrome } from '../components/admin/AdminChrome';
import {
  adminApi,
  useNoIndex,
  formatDate,
  formatDateTime,
  CANDIDATE_STATUSES,
  candidateStatusClass,
} from '../components/admin/adminApi';
import { jobOpenings } from '../data/careers';

// Helper to format file size
const formatFileSize = (bytes) => {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Helper to download base64 document or fallback to API URL
const downloadFilePayload = (fileObj, defaultName, candidateId, isCover = false) => {
  if (fileObj?.data_base64) {
    try {
      let b64 = fileObj.data_base64;
      if (b64.includes(',')) b64 = b64.split(',')[1];
      const byteCharacters = atob(b64);
      const byteNumbers = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([byteNumbers], {
        type: fileObj.content_type || 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileObj.filename || defaultName || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return;
    } catch (e) {
      console.warn('Base64 direct download failed, falling back to endpoint:', e);
    }
  }

  // Fallback to direct backend download endpoint
  if (candidateId) {
    const url = isCover
      ? adminApi.getCoverNoteUrl(candidateId, true)
      : adminApi.getResumeUrl(candidateId, true);
    window.open(url, '_blank');
  }
};

// Helper to preview file in browser
const previewFilePayload = (fileObj, candidateId, isCover = false) => {
  if (fileObj?.data_base64) {
    try {
      let b64 = fileObj.data_base64;
      if (b64.includes(',')) b64 = b64.split(',')[1];
      const byteCharacters = atob(b64);
      const byteNumbers = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([byteNumbers], {
        type: fileObj.content_type || 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      return;
    } catch (e) {
      console.warn('Base64 preview failed, falling back to endpoint:', e);
    }
  }

  if (candidateId) {
    const url = isCover
      ? adminApi.getCoverNoteUrl(candidateId, false)
      : adminApi.getResumeUrl(candidateId, false);
    window.open(url, '_blank');
  }
};

export default function AdminCareers() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  useNoIndex();

  const [applications, setApplications] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [dbOpportunities, setDbOpportunities] = useState(jobOpenings);
  const [loading, setLoading] = useState(true);

  // Filters
  const initialJobId = searchParams.get('job_id') || 'all';
  const [selectedJobId, setSelectedJobId] = useState(initialJobId);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Selected Candidate Drawer
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [internalNotes, setInternalNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [copiedEmail, setCopiedEmail] = useState(false);

  // Synchronize URL param with filter
  useEffect(() => {
    const paramJobId = searchParams.get('job_id');
    if (paramJobId && paramJobId !== selectedJobId) {
      setSelectedJobId(paramJobId);
    }
  }, [searchParams, selectedJobId]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Load candidate applications and stats
  const loadData = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedJobId && selectedJobId !== 'all') {
        params.append('job_id', selectedJobId);
      }
      if (selectedStatus && selectedStatus !== 'all') {
        params.append('status', selectedStatus);
      }
      if (debouncedSearch.trim()) {
        params.append('search', debouncedSearch.trim());
      }
      params.append('limit', '200');

      const [res, statsRes, oppsRes] = await Promise.all([
        adminApi.listCandidates(params.toString()),
        adminApi.getCareerStats().catch(() => null),
        adminApi.listOpportunities().catch(() => null),
      ]);

      if (res.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }

      setApplications(res.applications || []);
      setTotalCount(res.total ?? (res.applications || []).length);
      if (statsRes) setStats(statsRes);
      if (oppsRes?.opportunities && oppsRes.opportunities.length > 0) {
        setDbOpportunities(oppsRes.opportunities);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load candidate applications.');
    } finally {
      setLoading(false);
    }
  }, [selectedJobId, selectedStatus, debouncedSearch, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Update Drawer when selecting a candidate
  const openCandidateDrawer = (cand) => {
    setSelectedCandidate(cand);
    setInternalNotes(cand.notes || '');
    setConfirmDeleteId(null);
  };

  // Change Candidate Status
  const handleStatusChange = async (cand, newStatus) => {
    try {
      const updated = await adminApi.updateCandidate(cand.id, { status: newStatus });
      setApplications((prev) =>
        prev.map((item) => (item.id === cand.id ? { ...item, status: newStatus } : item))
      );
      if (selectedCandidate?.id === cand.id) {
        setSelectedCandidate((prev) => ({ ...prev, status: newStatus }));
      }
      toast.success(`Updated status to ${newStatus.toUpperCase()}`);
    } catch (e) {
      toast.error(e.message || 'Failed to update candidate status.');
    }
  };

  // Save Internal Recruiter Notes
  const handleSaveNotes = async () => {
    if (!selectedCandidate) return;
    setSavingNotes(true);
    try {
      await adminApi.updateCandidate(selectedCandidate.id, { notes: internalNotes });
      setApplications((prev) =>
        prev.map((item) =>
          item.id === selectedCandidate.id ? { ...item, notes: internalNotes } : item
        )
      );
      setSelectedCandidate((prev) => ({ ...prev, notes: internalNotes }));
      toast.success('Candidate notes saved.');
    } catch (e) {
      toast.error(e.message || 'Failed to save notes.');
    } finally {
      setSavingNotes(false);
    }
  };

  // Delete Candidate
  const handleDeleteCandidate = async (candId) => {
    try {
      await adminApi.deleteCandidate(candId);
      setApplications((prev) => prev.filter((item) => item.id !== candId));
      if (selectedCandidate?.id === candId) {
        setSelectedCandidate(null);
      }
      toast.success('Candidate application deleted.');
      loadData(false);
    } catch (e) {
      toast.error(e.message || 'Failed to delete application.');
    }
  };

  // Export CSV
  const handleExportCsv = () => {
    if (!applications.length) {
      toast.error('No candidate data to export.');
      return;
    }
    adminApi.exportCandidatesCsv(applications);
    toast.success(`Exported ${applications.length} candidate applications.`);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedJobId('all');
    setSelectedStatus('all');
    setSearch('');
    setSearchParams({});
  };

  // List of all opportunities for filter dropdown
  const allOpportunities = useMemo(() => {
    const list = [...dbOpportunities];
    // Also include any job_ids that exist in candidate applications but might not be in static list
    applications.forEach((app) => {
      if (app.job_id && !list.find((o) => o.id === app.job_id)) {
        list.push({
          id: app.job_id,
          title: app.job_title || app.job_id,
          department: 'General',
        });
      }
    });
    return list;
  }, [dbOpportunities, applications]);

  // Computed counts
  const shortlistedCount = useMemo(
    () => applications.filter((a) => a.status === 'shortlisted').length,
    [applications]
  );
  const reviewedCount = useMemo(
    () => applications.filter((a) => a.status === 'reviewed' || a.status === 'interviewed').length,
    [applications]
  );

  const isFiltered = selectedJobId !== 'all' || selectedStatus !== 'all' || Boolean(debouncedSearch);

  return (
    <AdminChrome
      title="Candidate Applications"
      subtitle={`${applications.length} candidate${applications.length === 1 ? '' : 's'}${
        isFiltered ? ' (filtered)' : ' total'
      }`}
      testId="admin-careers-page"
      actions={
        <>
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            onClick={handleExportCsv}
            disabled={applications.length === 0}
            data-testid="admin-export-candidates-csv"
          >
            <Download size={15} /> Export CSV
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-ghost"
            onClick={() => loadData(true)}
            aria-label="Refresh candidates list"
            title="Refresh candidate list"
            data-testid="admin-refresh-candidates"
          >
            <RefreshCw size={15} />
          </button>
        </>
      }
    >
      {/* 1. Summary Metrics Cards */}
      <div className="admin-cards">
        <div className="admin-card">
          <span>
            <Users size={14} /> Total Applicants
          </span>
          <strong>{stats?.total ?? totalCount}</strong>
          <small>Across all career opportunities</small>
        </div>

        <div className="admin-card">
          <span>
            <Briefcase size={14} /> In Current View
          </span>
          <strong>{applications.length}</strong>
          <small>{isFiltered ? 'Matching active filters' : 'Full applicant database'}</small>
        </div>

        <div className="admin-card">
          <span>
            <UserCheck size={14} /> Shortlisted
          </span>
          <strong>{stats?.by_status?.shortlisted ?? shortlistedCount}</strong>
          <small>Candidates marked for interview</small>
        </div>

        <div className="admin-card">
          <span>
            <Clock size={14} /> Under Review
          </span>
          <strong>
            {(stats?.by_status?.reviewed || 0) + (stats?.by_status?.interviewed || 0) ||
              reviewedCount}
          </strong>
          <small>Reviewed & interviewed profiles</small>
        </div>
      </div>

      {/* 2. Filter & Search Toolbar */}
      <div className="admin-toolbar" data-testid="admin-careers-toolbar">
        {/* Search */}
        <div className="admin-search">
          <Search size={16} />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search candidate name, email, phone, location…"
            data-testid="candidate-search-input"
          />
        </div>

        {/* Opportunity / Role Filter */}
        <select
          className="admin-control"
          value={selectedJobId}
          onChange={(e) => {
            setSelectedJobId(e.target.value);
            if (e.target.value === 'all') {
              const next = new URLSearchParams(searchParams);
              next.delete('job_id');
              setSearchParams(next);
            } else {
              setSearchParams({ ...Object.fromEntries(searchParams), job_id: e.target.value });
            }
          }}
          data-testid="candidate-role-filter"
        >
          <option value="all">All Opportunities ({stats?.total ?? allOpportunities.length})</option>
          {allOpportunities.map((opp) => {
            const count = stats?.by_job?.[opp.id]?.count;
            return (
              <option key={opp.id} value={opp.id}>
                {opp.title} {count !== undefined ? `(${count})` : ''}
              </option>
            );
          })}
        </select>

        {/* Status Filter */}
        <select
          className="admin-control"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          data-testid="candidate-status-filter"
        >
          <option value="all">All Statuses</option>
          {CANDIDATE_STATUSES.map((st) => (
            <option key={st} value={st}>
              {st.charAt(0).toUpperCase() + st.slice(1)}
            </option>
          ))}
        </select>

        {/* Clear Filters */}
        {isFiltered && (
          <button
            type="button"
            className="admin-clear"
            onClick={handleClearFilters}
            data-testid="clear-candidate-filters"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* 3. Candidate Applications Table (Desktop) */}
      <div className="admin-table-container">
        <table className="admin-candidate-table" data-testid="candidate-applications-table">
          <thead>
            <tr>
              <th>Candidate</th>
              <th>Applied Opportunity</th>
              <th>Location</th>
              <th>Experience</th>
              <th>Resume / CV</th>
              <th>Portfolio / Link</th>
              <th>Applied Date</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} style={{ padding: '40px', textAlign: 'center' }}>
                  <div className="admin-skeleton" style={{ width: '100%', height: '40px', borderRadius: '8px' }} />
                </td>
              </tr>
            ) : applications.length === 0 ? (
              <tr>
                <td colSpan={9}>
                  <div className="admin-empty-state">
                    <h3>No candidate applications found</h3>
                    <p>
                      {isFiltered
                        ? 'Try clearing or changing your filters to see more applicants.'
                        : 'No candidates have submitted their profile yet.'}
                    </p>
                    {isFiltered && (
                      <button
                        type="button"
                        className="admin-btn admin-btn-ghost"
                        style={{ marginTop: '14px' }}
                        onClick={handleClearFilters}
                      >
                        Reset filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              applications.map((cand) => {
                const initials = (cand.full_name || 'Candidate')
                  .split(' ')
                  .map((w) => w[0])
                  .filter(Boolean)
                  .slice(0, 2)
                  .join('')
                  .toUpperCase();

                const isSelected = selectedCandidate?.id === cand.id;

                return (
                  <tr
                    key={cand.id}
                    className={isSelected ? 'is-selected' : ''}
                    onClick={() => openCandidateDrawer(cand)}
                    data-testid={`candidate-row-${cand.id}`}
                  >
                    {/* Candidate Info */}
                    <td>
                      <div className="candidate-info-cell">
                        <div className="candidate-avatar">{initials}</div>
                        <div className="candidate-names">
                          <strong>{cand.full_name}</strong>
                          <span>{cand.email}</span>
                          {cand.phone && <small style={{ color: '#8291a7' }}>{cand.phone}</small>}
                        </div>
                      </div>
                    </td>

                    {/* Applied Opportunity */}
                    <td>
                      <span className="admin-role-badge" title={cand.job_title}>
                        <Briefcase size={12} /> {cand.job_title}
                      </span>
                    </td>

                    {/* Location */}
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        <MapPin size={13} color="#8ea3c4" />
                        {cand.location || '—'}
                      </span>
                    </td>

                    {/* Experience */}
                    <td>
                      <span style={{ fontWeight: 600 }}>{cand.experience_years || '—'}</span>
                    </td>

                    {/* Resume / CV */}
                    <td onClick={(e) => e.stopPropagation()}>
                      {cand.resume ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            type="button"
                            className="admin-file-btn"
                            onClick={() => previewFilePayload(cand.resume, cand.id, false)}
                            title="Preview resume in new tab"
                            data-testid={`preview-resume-${cand.id}`}
                          >
                            <FileText size={13} />
                            View
                          </button>
                          <button
                            type="button"
                            className="admin-file-btn"
                            style={{ padding: '5px 8px' }}
                            onClick={() =>
                              downloadFilePayload(cand.resume, cand.resume.filename, cand.id, false)
                            }
                            title="Download resume file"
                            data-testid={`download-resume-${cand.id}`}
                          >
                            <Download size={13} />
                          </button>
                        </div>
                      ) : (
                        <span style={{ color: '#7a889e', fontSize: '12px' }}>Not uploaded</span>
                      )}
                    </td>

                    {/* Portfolio / Link */}
                    <td onClick={(e) => e.stopPropagation()}>
                      {cand.portfolio_url ? (
                        <a
                          href={cand.portfolio_url}
                          target="_blank"
                          rel="noreferrer"
                          className="admin-link-btn"
                          data-testid={`portfolio-link-${cand.id}`}
                        >
                          <ExternalLink size={13} /> Open
                        </a>
                      ) : (
                        <span style={{ color: '#7a889e' }}>—</span>
                      )}
                    </td>

                    {/* Applied Date */}
                    <td>
                      <span style={{ fontSize: '12.5px', color: '#97a6bd' }}>
                        {formatDate(cand.created_at)}
                      </span>
                    </td>

                    {/* Status Pill Menu */}
                    <td onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className={`status-pill ${candidateStatusClass(cand.status)}`}
                            data-testid={`candidate-status-${cand.id}`}
                          >
                            <i aria-hidden="true" />
                            {(cand.status || 'submitted').toUpperCase()}
                            <ChevronDown size={13} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="status-menu">
                          {CANDIDATE_STATUSES.map((option) => (
                            <DropdownMenuItem
                              key={option}
                              onClick={() => handleStatusChange(cand, option)}
                              data-testid={`set-status-${cand.id}-${option}`}
                            >
                              {option.toUpperCase()}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>

                    {/* Actions */}
                    <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <button
                          type="button"
                          className="admin-row-menu"
                          onClick={() => openCandidateDrawer(cand)}
                          title="View candidate details"
                          aria-label="View details"
                          data-testid={`view-details-${cand.id}`}
                        >
                          <Eye size={15} />
                        </button>
                        <button
                          type="button"
                          className="admin-row-menu"
                          style={{ color: '#ff9494' }}
                          onClick={() => handleDeleteCandidate(cand.id)}
                          title="Delete candidate application"
                          aria-label="Delete"
                          data-testid={`delete-cand-${cand.id}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* 4. Candidate Applications Cards (Mobile / Tablet fallback) */}
      <div className="admin-cardlist" style={{ marginTop: '16px' }}>
        {applications.map((cand) => (
          <div
            key={cand.id}
            className="admin-entry-card"
            onClick={() => openCandidateDrawer(cand)}
            data-testid={`candidate-card-${cand.id}`}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              <h3>{cand.full_name}</h3>
              <span className={`status-pill ${candidateStatusClass(cand.status)}`} style={{ minHeight: '28px' }}>
                <i /> {(cand.status || 'submitted').toUpperCase()}
              </span>
            </div>
            <p style={{ color: '#8fb2ff', fontWeight: 600, marginTop: '4px' }}>{cand.job_title}</p>
            <p>{cand.email} • {cand.phone || 'No phone'}</p>
            <div className="admin-entry-card-foot">
              <small>{cand.location} • Exp: {cand.experience_years}</small>
              <small>{formatDate(cand.created_at)}</small>
            </div>
          </div>
        ))}
      </div>

      {/* 5. Candidate Details Slide-Out Drawer */}
      <Sheet
        open={Boolean(selectedCandidate)}
        onOpenChange={(open) => {
          if (!open) setSelectedCandidate(null);
        }}
      >
        <SheetContent side="right" className="admin-drawer" data-testid="candidate-drawer">
          {selectedCandidate && (
            <>
              {/* Drawer Top Header */}
              <div className="admin-drawer-head">
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div className="candidate-avatar" style={{ width: '48px', height: '48px', fontSize: '16px' }}>
                    {(selectedCandidate.full_name || 'C')
                      .split(' ')
                      .map((w) => w[0])
                      .slice(0, 2)
                      .join('')
                      .toUpperCase()}
                  </div>
                  <div>
                    <SheetTitle asChild>
                      <h2>{selectedCandidate.full_name}</h2>
                    </SheetTitle>
                    <p style={{ marginTop: '2px' }}>
                      Applied for <strong style={{ color: '#fff' }}>{selectedCandidate.job_title}</strong>
                    </p>
                  </div>
                </div>

                {/* Quick actions banner */}
                <div className="admin-drawer-actions">
                  {/* Status Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className={`status-pill ${candidateStatusClass(selectedCandidate.status)}`}
                      >
                        <i /> {(selectedCandidate.status || 'submitted').toUpperCase()}
                        <ChevronDown size={13} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="status-menu">
                      {CANDIDATE_STATUSES.map((option) => (
                        <DropdownMenuItem
                          key={option}
                          onClick={() => handleStatusChange(selectedCandidate, option)}
                        >
                          {option.toUpperCase()}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Email action */}
                  <a
                    className="admin-btn admin-btn-ghost"
                    href={`mailto:${selectedCandidate.email}`}
                    title="Send Email"
                  >
                    <Mail size={14} /> Email
                  </a>

                  {/* Copy Email */}
                  <button
                    type="button"
                    className="admin-btn admin-btn-ghost"
                    onClick={() => {
                      navigator.clipboard?.writeText(selectedCandidate.email);
                      setCopiedEmail(true);
                      setTimeout(() => setCopiedEmail(false), 1800);
                    }}
                  >
                    {copiedEmail ? <Check size={14} /> : <Copy size={14} />}
                    {copiedEmail ? 'Copied' : 'Copy'}
                  </button>

                  {/* Phone action */}
                  {selectedCandidate.phone && (
                    <a
                      className="admin-btn admin-btn-ghost"
                      href={`tel:${selectedCandidate.phone.replace(/[^+\d]/g, '')}`}
                      title="Call Candidate"
                    >
                      <Phone size={14} /> Call
                    </a>
                  )}

                  {/* Delete button */}
                  {confirmDeleteId === selectedCandidate.id ? (
                    <div style={{ display: 'inline-flex', gap: '6px' }}>
                      <button
                        type="button"
                        className="admin-btn admin-btn-danger"
                        onClick={() => handleDeleteCandidate(selectedCandidate.id)}
                      >
                        <Trash2 size={14} /> Confirm
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn-ghost"
                        onClick={() => setConfirmDeleteId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="admin-btn admin-btn-danger"
                      onClick={() => setConfirmDeleteId(selectedCandidate.id)}
                      title="Delete Candidate Application"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  )}
                </div>
              </div>

              {/* Drawer Body Details */}
              <div className="admin-drawer-body">
                {/* 1. Contact & Role Info Section */}
                <div className="detail-section">
                  <div className="detail-section-title">
                    <Building2 size={13} /> Candidate Profile Details
                  </div>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-item-label">Full Name</span>
                      <span className="detail-item-value">{selectedCandidate.full_name}</span>
                    </div>

                    <div className="detail-item">
                      <span className="detail-item-label">Applied Opportunity</span>
                      <span className="detail-item-value" style={{ color: '#8fb4ff' }}>
                        {selectedCandidate.job_title}
                      </span>
                    </div>

                    <div className="detail-item">
                      <span className="detail-item-label">Email Address</span>
                      <span className="detail-item-value">{selectedCandidate.email}</span>
                    </div>

                    <div className="detail-item">
                      <span className="detail-item-label">Phone Number</span>
                      <span className="detail-item-value">{selectedCandidate.phone || '—'}</span>
                    </div>

                    <div className="detail-item">
                      <span className="detail-item-label">Current Location</span>
                      <span className="detail-item-value">{selectedCandidate.location || '—'}</span>
                    </div>

                    <div className="detail-item">
                      <span className="detail-item-label">Relevant Experience</span>
                      <span className="detail-item-value">{selectedCandidate.experience_years || '—'}</span>
                    </div>

                    <div className="detail-item">
                      <span className="detail-item-label">Applied Date</span>
                      <span className="detail-item-value">
                        {formatDateTime(selectedCandidate.created_at)}
                      </span>
                    </div>

                    <div className="detail-item">
                      <span className="detail-item-label">Portfolio / LinkedIn</span>
                      <span className="detail-item-value">
                        {selectedCandidate.portfolio_url ? (
                          <a
                            href={selectedCandidate.portfolio_url}
                            target="_blank"
                            rel="noreferrer"
                            className="admin-link-btn"
                          >
                            <ExternalLink size={13} /> Visit Profile
                          </a>
                        ) : (
                          '—'
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 2. Resume / CV Attachment Section */}
                <div className="detail-section">
                  <div className="detail-section-title">
                    <FileText size={13} /> Candidate Resume / CV Document
                  </div>
                  {selectedCandidate.resume ? (
                    <div className="file-attachment-card">
                      <div className="file-info-group">
                        <div className="file-icon-box">
                          <FileText size={20} />
                        </div>
                        <div className="file-meta-text">
                          <strong>{selectedCandidate.resume.filename || 'Candidate_Resume.pdf'}</strong>
                          <span>
                            {selectedCandidate.resume.content_type || 'PDF Document'}
                            {selectedCandidate.resume.size_bytes
                              ? ` • ${formatFileSize(selectedCandidate.resume.size_bytes)}`
                              : ''}
                          </span>
                        </div>
                      </div>

                      <div className="file-actions-group">
                        <button
                          type="button"
                          className="admin-btn admin-btn-ghost"
                          style={{ minHeight: '38px', padding: '0 12px' }}
                          onClick={() => previewFilePayload(selectedCandidate.resume, selectedCandidate.id, false)}
                          title="Open Resume in new browser tab"
                        >
                          <ExternalLink size={14} /> Preview
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn-primary"
                          style={{ minHeight: '38px', padding: '0 12px' }}
                          onClick={() =>
                            downloadFilePayload(
                              selectedCandidate.resume,
                              selectedCandidate.resume.filename,
                              selectedCandidate.id,
                              false
                            )
                          }
                          title="Download Resume to computer"
                        >
                          <Download size={14} /> Download
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="admin-empty-state" style={{ padding: '24px' }}>
                      <AlertCircle size={24} color="#f59e0b" style={{ margin: '0 auto 8px' }} />
                      <p>No resume file found for this applicant.</p>
                    </div>
                  )}
                </div>

                {/* 3. Cover Note Section */}
                <div className="detail-section">
                  <div className="detail-section-title">
                    <FileCheck size={13} /> Cover Note / Candidate Statement
                  </div>
                  {selectedCandidate.cover_note ? (
                    typeof selectedCandidate.cover_note === 'object' &&
                    selectedCandidate.cover_note.data_base64 ? (
                      <div className="file-attachment-card">
                        <div className="file-info-group">
                          <div className="file-icon-box" style={{ background: 'rgba(160,124,255,.15)', color: '#c7b1ff' }}>
                            <FileText size={20} />
                          </div>
                          <div className="file-meta-text">
                            <strong>{selectedCandidate.cover_note.filename || 'Cover_Letter.pdf'}</strong>
                            <span>
                              {formatFileSize(selectedCandidate.cover_note.size_bytes) || 'Attached Document'}
                            </span>
                          </div>
                        </div>
                        <div className="file-actions-group">
                          <button
                            type="button"
                            className="admin-btn admin-btn-ghost"
                            onClick={() =>
                              downloadFilePayload(
                                selectedCandidate.cover_note,
                                selectedCandidate.cover_note.filename,
                                selectedCandidate.id,
                                true
                              )
                            }
                          >
                            <Download size={14} /> Download
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="cover-note-box">
                        {selectedCandidate.cover_note.text ||
                          (typeof selectedCandidate.cover_note === 'string'
                            ? selectedCandidate.cover_note
                            : 'No written cover note provided.')}
                      </div>
                    )
                  ) : (
                    <p style={{ fontSize: '13px', color: '#8897ae', marginTop: '6px' }}>
                      No cover note was attached by the candidate.
                    </p>
                  )}
                </div>

                {/* 4. Internal Recruiter Notes */}
                <div className="detail-section">
                  <div className="detail-section-title">
                    <UserCheck size={13} /> Internal Recruiter Evaluation
                  </div>
                  <div className="admin-note-block" style={{ marginTop: '4px' }}>
                    <label htmlFor="recruiter-notes">
                      Evaluation Notes & Interview Next Steps
                      <em>{savingNotes ? 'Saving…' : 'Private to Admin'}</em>
                    </label>
                    <textarea
                      id="recruiter-notes"
                      value={internalNotes}
                      onChange={(e) => setInternalNotes(e.target.value)}
                      placeholder="Candidate screening feedback, strengths, interview stage, compensation expectations…"
                      rows={4}
                    />
                    <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="admin-btn admin-btn-primary"
                        onClick={handleSaveNotes}
                        disabled={savingNotes}
                      >
                        Save Evaluation
                      </button>
                    </div>
                  </div>
                </div>

                {/* 5. Direct Job Posting Link */}
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <Link
                    to={`/careers/${selectedCandidate.job_id}`}
                    target="_blank"
                    className="admin-link-btn"
                  >
                    <ExternalLink size={13} /> View Live Opportunity Job Posting
                  </Link>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </AdminChrome>
  );
}
